import React, { useMemo, useRef, useState } from 'react';

import { Tabs, TabsVariant } from '@lux/tabs';
import { TranslateScope } from '@pegasus/translation';
import { getSessionStorageItem, setSessionStorageItem } from '@pegasus/utils';
import { ErrorBoundary } from '@browsing/components';

import { setCrossCampaignFiltersCategoryId } from '../mylounge/cross-campaign/utils';
import { useContinueShopping } from './hooks/use-continue-shopping';
import { ContinueShoppingCardItem } from './types';
import { prepareContinueShoppingData, mapCardItemToTrackingItem } from './helpers';
import { CategoryTabs, CategoryPanel } from './components';
import { Title } from './components/title';
import { useTracking } from './hooks/use-tracking';
import { useViewTracking } from './hooks/use-view-tracking';
import { SELECTED_TAB_STORAGE_KEY } from './constants';

import * as S from './components/styles';

export const ContinueShoppingCarouselInternal: React.FC = () => {
    const { trackArticleClick, trackSeeAllClick, trackTabChange, trackSwipe } = useTracking();
    const { continueShoppingData } = useContinueShopping();

    const { panelData } = useMemo(
        () => prepareContinueShoppingData(continueShoppingData),
        [continueShoppingData],
    );

    const containerRef = useRef<HTMLDivElement>(null);

    const initialTabId = useMemo(() => {
        if (panelData.length === 0) {
            return '';
        }
        const savedTab = getSessionStorageItem(SELECTED_TAB_STORAGE_KEY);
        const validTabIds = panelData.map(item => item.category.categoryId.toString());
        if (savedTab && validTabIds.includes(savedTab)) {
            return savedTab;
        }

        return panelData[0].category.categoryId.toString();
    }, [panelData]);

    const [currentTabId, setCurrentTabId] = useState<string>(initialTabId);

    const currentTabTrackingItems = useMemo(() => {
        const currentPanel = panelData.find(
            panel => panel.category.categoryId.toString() === currentTabId,
        );
        if (!currentPanel) {
            return [];
        }
        return currentPanel.categoriesList.map(mapCardItemToTrackingItem);
    }, [panelData, currentTabId]);

    useViewTracking(containerRef, currentTabTrackingItems, currentTabId);

    if (panelData.length === 0) {
        return null;
    }

    const firstCategory = panelData[0].category;

    const handleClick = (article: ContinueShoppingCardItem, index: number) => {
        setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, currentTabId);
        trackArticleClick(article, index);
    };

    const handleSeeAllClick = (categoryId: number) => {
        setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, currentTabId);
        trackSeeAllClick();
        setCrossCampaignFiltersCategoryId(categoryId);
    };

    const handleTabChange = (_params: { index: number; value: string }) => {
        if (_params.index < panelData.length) {
            const selectedCategory = panelData[_params.index]?.category;
            if (selectedCategory) {
                const tabId = selectedCategory.categoryId.toString();
                setCurrentTabId(tabId);
                setSessionStorageItem(SELECTED_TAB_STORAGE_KEY, tabId);
                trackTabChange(tabId);
            }
        }
    };

    const handleSwipe = () => {
        trackSwipe();
    };

    return (
        <S.ContinueShoppingContainer ref={containerRef} data-testid="continue-shopping-carousel">
            <TranslateScope path="continueShopping">
                <Title
                    isSingleCategory={panelData.length === 1}
                    firstCategoryName={firstCategory.categoryName}
                />

                <Tabs initialTab={initialTabId} variant={TabsVariant.Segmented}>
                    {panelData.length > 1 && (
                        <CategoryTabs
                            categories={panelData.map(item => item.category)}
                            onTabChange={handleTabChange}
                        />
                    )}

                    {panelData.map(({ category, shouldShowSeeAll, categoriesList }) => (
                        <CategoryPanel
                            key={category.categoryId}
                            category={category}
                            categoriesList={categoriesList}
                            shouldShowSeeAll={shouldShowSeeAll}
                            onArticleClick={handleClick}
                            onSeeAllClick={handleSeeAllClick}
                            onScroll={handleSwipe}
                        />
                    ))}
                </Tabs>
            </TranslateScope>
        </S.ContinueShoppingContainer>
    );
};

export const ContinueShoppingCarousel: React.FC = () => (
    <ErrorBoundary componentName="ContinueShoppingCarousel">
        <ContinueShoppingCarouselInternal />
    </ErrorBoundary>
);
